import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, RotateCcw, Eye } from 'lucide-react';
import Modal, { ModalFormGrid, ModalFormColumn, FormGroup } from '../components/common/Modal';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';
import serviceService from '../services/serviceService';
import serviceCategoryService from '../services/serviceCategoryService';
import '../styles/FormSections.css';

const ServiceListPage = () => {
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();
  
  // Estados
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [viewingService, setViewingService] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    paymentType: '',
    isActive: 'true'
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    paymentType: 'ONE_TIME',
    billingPeriod: '',
    category: '',
    notes: '',
    isActive: true
  });

  // Cargar servicios
  const loadServices = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: itemsPerPage,
        search: searchTerm,
        category: filters.category,
        paymentType: filters.paymentType,
        isActive: filters.isActive
      };
      
      const data = await serviceService.getServices(params);
      setServices(data.services || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalItems(data.pagination?.totalItems || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error cargando servicios:', error);
      showNotification('Error al cargar los servicios', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, itemsPerPage, showNotification]);

  // Cargar categorías
  const loadCategories = useCallback(async () => {
    try {
      const data = await serviceCategoryService.getActiveCategories();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Error cargando categorías:', error);
    }
  }, []);

  // Abrir modal de creación
  const openCreateModal = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      paymentType: 'ONE_TIME',
      billingPeriod: '',
      category: '',
      notes: '',
      isActive: true
    });
    setIsEditing(false);
    setEditingService(null);
    setShowModal(true);
  };

  // Abrir modal de visualización
  const openViewModal = (service) => {
    setViewingService(service);
    setShowViewModal(true);
  };

  // Abrir modal de edición
  const openEditModal = (service) => {
    setFormData({
      name: service.name,
      description: service.description || '',
      price: service.price.toString(),
      paymentType: service.paymentType,
      billingPeriod: service.billingPeriod || '',
      category: service.category._id,
      notes: service.notes || '',
      isActive: service.isActive
    });
    setIsEditing(true);
    setEditingService(service);
    setShowModal(true);
  };

  // Cerrar modal
  const closeModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setEditingService(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      paymentType: 'ONE_TIME',
      billingPeriod: '',
      category: '',
      notes: '',
      isActive: true
    });
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Preparar datos para envío
      const submitData = { ...formData };
      if (submitData.paymentType === 'ONE_TIME') {
        delete submitData.billingPeriod;
      }
      
      let data;
      if (isEditing) {
        data = await serviceService.updateService(editingService._id, submitData);
      } else {
        data = await serviceService.createService(submitData);
      }
      
      showNotification(data.message, 'success');
      closeModal();
      loadServices();
    } catch (error) {
      console.error('Error guardando servicio:', error);
      showNotification(error.message || 'Error al guardar el servicio', 'error');
    }
  };

  // Confirmar eliminación de servicio
  const confirmDelete = (service) => {
    setServiceToDelete(service);
    setShowDeleteModal(true);
  };

  // Ejecutar eliminación
  const executeDelete = async () => {
    if (!serviceToDelete) return;
    try {
      const data = await serviceService.deleteService(serviceToDelete._id);
      showNotification(data.message, 'success');
      setShowDeleteModal(false);
      setServiceToDelete(null);
      loadServices();
    } catch (error) {
      console.error('Error eliminando servicio:', error);
      showNotification(error.message || 'Error al eliminar el servicio', 'error');
    }
  };

  // Restaurar servicio
  const handleRestore = async (serviceId, serviceName) => {
    try {
      const data = await serviceService.restoreService(serviceId);
      showNotification(data.message, 'success');
      loadServices();
    } catch (error) {
      console.error('Error restaurando servicio:', error);
      showNotification(error.message || 'Error al restaurar el servicio', 'error');
    }
  };

  // Manejar filtros
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ category: '', paymentType: '', isActive: 'true' });
    setCurrentPage(1);
  };

  // Navegación de páginas
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadServices(newPage);
    }
  };

  // Cambiar página anterior
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  // Cambiar página siguiente
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Manejar cambio de registros por página
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    loadServices(1);
  };

  // Los servicios ya vienen filtrados del servidor
  const filteredServices = services;

  // Cargar datos iniciales
  useEffect(() => {
    loadServices(1);
    loadCategories();
  }, [loadServices, loadCategories]);

  // Recargar cuando cambien los filtros o búsqueda
  useEffect(() => {
    if (currentPage === 1) {
      loadServices(1);
    } else {
      setCurrentPage(1);
      loadServices(1);
    }
  }, [searchTerm, filters]);

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Servicios de RedXtreme</h1>
          <p className="text-gray-600">
            Gestiona el catálogo de servicios que ofrece la empresa
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/services/trash')}
            className="btn btn-outline"
            title="Papelera"
          >
            🗑️ Papelera
          </button>
          <button
            onClick={() => navigate('/services/categories')}
            className="btn btn-outline"
            title="Gestionar categorías"
          >
            📂 Categorías
          </button>
          <button
            onClick={openCreateModal}
            className="btn btn-primary"
          >
            <Plus size={16} />
            Nuevo Servicio
          </button>
        </div>
      </div>

      {/* Contenedor de filtros */}
      <div className="card mb-6 filters-container">
        <div className="card-content">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Campo de búsqueda */}
            <div className="flex-1 min-w-64">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nombre, descripción o notas..."
                className="form-input"
              />
            </div>

            {/* Filtro por categoría */}
            <div className="w-48">
              <CustomSelect
                label="Categoría"
                value={filters.category}
                onChange={(value) => handleFilterChange('category', value)}
                options={[
                  { value: '', label: 'Todas las categorías' },
                  ...categories.map(cat => ({ value: cat._id, label: cat.name }))
                ]}
                placeholder="Todas las categorías"
              />
            </div>

            {/* Filtro por tipo de pago */}
            <div className="w-48">
              <CustomSelect
                label="Tipo de Pago"
                value={filters.paymentType}
                onChange={(value) => handleFilterChange('paymentType', value)}
                options={[
                  { value: '', label: 'Todos los tipos' },
                  { value: 'ONE_TIME', label: 'Pago Único' },
                  { value: 'RECURRING', label: 'Pago Recurrente' }
                ]}
                placeholder="Todos los tipos"
              />
            </div>

            {/* Filtro por estado */}
            <div className="w-48">
              <CustomSelect
                label="Estado"
                value={filters.isActive}
                onChange={(value) => handleFilterChange('isActive', value)}
                options={[
                  { value: '', label: 'Todos los estados' },
                  { value: 'true', label: 'Activo' },
                  { value: 'false', label: 'Inactivo' }
                ]}
                placeholder="Todos los estados"
              />
            </div>

            {/* Botón limpiar */}
            <div className="w-48">
              <label className="form-label">&nbsp;</label>
              <button
                onClick={clearFilters}
                className="btn btn-outline w-full"
              >
                🗑️ Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Selector de registros por página */}
      <div className="records-per-page">
        <label htmlFor="itemsPerPage">Registros por página:</label>
        <select
          id="itemsPerPage"
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      {/* Lista de servicios */}
      <div className="table-container">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="loading-spinner"></div>
            <p className="mt-4 text-gray-600">Cargando servicios...</p>
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">⚙️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || filters.category || filters.paymentType || (filters.isActive && filters.isActive !== 'true') ? 'No se encontraron resultados' : 'No hay servicios'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filters.category || filters.paymentType || (filters.isActive && filters.isActive !== 'true') ? 
                'No hay servicios que coincidan con los filtros aplicados' : 
                'Comienza agregando tu primer servicio'
              }
            </p>
            {!searchTerm && !filters.category && !filters.paymentType && (!filters.isActive || filters.isActive === 'true') && (
              <button
                onClick={openCreateModal}
                className="btn btn-primary"
              >
                <Plus size={16} />
                Agregar Servicio
              </button>
            )}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Categoría</th>
                <th>Precio</th>
                <th>Tipo de Pago</th>
                <th>Estado</th>
                <th>Creado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredServices.map((service) => (
                <tr key={service._id}>
                  <td>
                    <div className="font-medium text-gray-900">{service.name}</div>
                  </td>
                  <td>
                    <div 
                      className="text-sm text-gray-500" 
                      title={service.description || ''} 
                      style={{ 
                        maxWidth: '250px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word'
                      }}
                    >
                      {service.description || 'Sin descripción'}
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-primary">{service.category.name}</span>
                  </td>
                  <td className="text-sm text-gray-700">
                    ${service.price.toLocaleString()}
                  </td>
                  <td>
                    <div className="flex flex-col">
                      <span className={`badge ${service.paymentType === 'ONE_TIME' ? 'badge-secondary' : 'badge-success'}`}>
                        {service.paymentType === 'ONE_TIME' ? 'Pago Único' : 'Recurrente'}
                      </span>
                      {service.paymentType === 'RECURRING' && service.billingPeriod && (
                        <span className="text-xs text-gray-500 mt-1">
                          {service.billingPeriodText}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${service.isActive ? 'badge-success' : 'badge-secondary'}`}>
                      {service.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="text-sm text-gray-700">
                    {new Date(service.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openViewModal(service)}
                        className="btn btn-primary btn-sm"
                        title="Ver detalles"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => openEditModal(service)}
                        className="btn btn-sm"
                        style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: 'white' }}
                        title="Editar"
                      >
                        <Edit size={16} />
                      </button>
                      {service.isActive ? (
                        <button
                          onClick={() => confirmDelete(service)}
                          className="btn btn-danger btn-sm"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRestore(service._id, service.name)}
                          className="btn btn-success btn-sm"
                          title="Restaurar"
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>
              Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} servicios
            </span>
          </div>
          <div className="pagination-controls">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="btn btn-outline btn-sm"
            >
              ← Anterior
            </button>
            
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="btn btn-outline btn-sm"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {/* Modal de creación/edición */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={isEditing ? 'Editar Servicio' : 'Crear Servicio'}
        onSubmit={handleSubmit}
        submitText={isEditing ? 'Actualizar' : 'Crear'}
      >
        {/* Información Básica */}
        <div className="form-section">
          <h4 className="form-section-title">Información Básica</h4>
          <ModalFormGrid>
            <ModalFormColumn>
              <FormGroup label="Nombre del Servicio" required htmlFor="name">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Ej. Desarrollo de Página Web"
                  required
                />
              </FormGroup>
              
              <FormGroup label="Precio" required htmlFor="price">
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="0"
                  min="0"
                  step="0.01"
                  required
                />
              </FormGroup>
            </ModalFormColumn>

            <ModalFormColumn>
              <CustomSelect
                label="Categoría"
                value={formData.category}
                onChange={(value) => handleInputChange({ target: { name: 'category', value } })}
                options={[
                  { value: '', label: 'Seleccionar categoría' },
                  ...categories.map(cat => ({
                    value: cat._id,
                    label: cat.name
                  }))
                ]}
                placeholder="Seleccionar categoría"
              />
            </ModalFormColumn>
          </ModalFormGrid>

          <FormGroup label="Descripción" htmlFor="description">
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-input form-textarea"
              placeholder="Descripción detallada del servicio"
              rows="3"
            />
          </FormGroup>
        </div>

        {/* Tipo de Pago */}
        <div className="form-section">
          <h4 className="form-section-title">Tipo de Pago</h4>
          <ModalFormGrid>
            <ModalFormColumn>
              <CustomSelect
                label="Tipo de Pago"
                value={formData.paymentType}
                onChange={(value) => handleInputChange({ target: { name: 'paymentType', value } })}
                options={[
                  { value: 'ONE_TIME', label: 'Pago Único' },
                  { value: 'RECURRING', label: 'Pago Recurrente' }
                ]}
                placeholder="Seleccionar tipo de pago"
              />
            </ModalFormColumn>

            <ModalFormColumn>
              {formData.paymentType === 'RECURRING' && (
                <CustomSelect
                  label="Periodicidad"
                  value={formData.billingPeriod}
                  onChange={(value) => handleInputChange({ target: { name: 'billingPeriod', value } })}
                  options={[
                    { value: '', label: 'Seleccionar periodicidad' },
                    { value: 'WEEKLY', label: 'Semanal' },
                    { value: 'BIWEEKLY', label: 'Quincenal' },
                    { value: 'MONTHLY', label: 'Mensual' },
                    { value: 'YEARLY', label: 'Anual' }
                  ]}
                  placeholder="Seleccionar periodicidad"
                />
              )}
            </ModalFormColumn>
          </ModalFormGrid>
        </div>

        {/* Información Adicional */}
        <div className="form-section">
          <h4 className="form-section-title">Información Adicional</h4>
          <FormGroup label="Notas" htmlFor="notes">
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="form-input form-textarea"
              placeholder="Notas adicionales sobre el servicio"
              rows="2"
            />
          </FormGroup>
        </div>

        {/* Estado */}
        <div className="form-section">
          <h4 className="form-section-title">Estado</h4>
          <CustomSelect
            label="Estado"
            value={formData.isActive}
            onChange={(value) => handleInputChange({ target: { name: 'isActive', value } })}
            options={[
              { value: 'true', label: 'Activo' },
              { value: 'false', label: 'Inactivo' }
            ]}
            placeholder="Seleccionar estado"
          />
        </div>
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setServiceToDelete(null);
        }}
        title="Confirmar Eliminación"
        onSubmit={(e) => { e.preventDefault(); executeDelete(); }}
        submitText="Eliminar"
        cancelText="Cancelar"
        submitButtonClass="btn-danger"
      >
        <div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
          <p className="font-medium text-lg mb-2">¿Estás seguro de que quieres eliminar este servicio?</p>
          <p>Se eliminará el servicio: <strong>{serviceToDelete?.name}</strong>.</p>
        </div>
      </Modal>

      {/* Modal de visualización */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Detalles del Servicio"
        onSubmit={(e) => { e.preventDefault(); setShowViewModal(false); }}
        submitText="Cerrar"
        hideCancel={true}
      >
        {viewingService && (
          <div className="space-y-4 text-left">
            <div>
              <h4 className="text-sm font-semibold text-gray-500">Nombre</h4>
              <p className="text-base text-gray-900 font-medium">{viewingService.name}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-500">Categoría</h4>
              <p className="text-base text-gray-900 mt-1">
                <span className="badge badge-primary">{viewingService.category?.name}</span>
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-500">Descripción detallada</h4>
              <p className="text-base text-gray-900 mt-1 whitespace-pre-wrap p-3 bg-gray-50 rounded-md border border-gray-100">{viewingService.description || 'Sin descripción'}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-500">Precio</h4>
                <p className="text-base font-medium text-gray-900">${viewingService.price.toLocaleString()}</p>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-500">Tipo de Pago</h4>
                <p className="text-base text-gray-900">
                  {viewingService.paymentType === 'ONE_TIME' ? 'Pago Único' : `Recurrente (${viewingService.billingPeriodText || 'mensual'})`}
                </p>
              </div>
            </div>
            {viewingService.notes && (
              <div>
                <h4 className="text-sm font-semibold text-gray-500">Notas</h4>
                <p className="text-base text-gray-900 mt-1 whitespace-pre-wrap p-3 bg-gray-50 rounded-md border border-gray-100">{viewingService.notes}</p>
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-gray-500">Estado</h4>
              <p className="text-base text-gray-900 mt-1">
                <span className={`badge ${viewingService.isActive ? 'badge-success' : 'badge-secondary'}`}>
                  {viewingService.isActive ? 'Activo' : 'Inactivo'}
                </span>
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Notificación */}
      <NotificationSystem 
        notification={notification} 
        onClose={hideNotification} 
      />
    </div>
  );
};

export default ServiceListPage;